import { Injectable } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { PaperworkState } from '@store/reducers';
import { HttpClient } from '@angular/common/http';
import { environment } from '@environments/environment';
import { catchError, map, retry } from 'rxjs/operators';
import { PdfService } from '@shared/helpers/pdf.helper';
import { AuthService } from '@auth/services/auth.service';
import { API_URIS } from '@shared/constants/api-uris.const';
import { Paperwork } from '@shared/interfaces/paperwork.interface';
import { PCV2Request } from '@shared/interfaces/request.interface';
import { FileExtension } from '@shared/constants/mime-types.const';
import { Derivation } from '@shared/interfaces/derivation.interface';
import { buildContentPdfDerivation } from '@clinical/derivations/utils/build-pdf-derivation';

const BASE_API_PCV2: string = environment.baseApiPCv2;
const URI_KEIRON_NOTIFICATION: string = API_URIS.keiron.notification;
const URI_CLINICAL_DERIVATION: string = API_URIS.clinical.derivations;

@Injectable({
  providedIn: 'root',
})
export class DerivationService {

  constructor(
    private pdfService: PdfService,
    private httpClient: HttpClient,
    private authService: AuthService
  ) { }

  save(body: Paperwork<Derivation>): Observable<Paperwork<Derivation>> {
    return this.httpClient.post<PCV2Request>(`${BASE_API_PCV2}/${URI_CLINICAL_DERIVATION}`, body).pipe(
      retry(3),
      map(({ data }: { data: Paperwork<Derivation> }) => data),
    );
  };

  /**
   * Notifica a Keiron enviando los datos proporcionados en un objeto FormData.
   *
   * @param formData - Los datos del formulario que se enviarán en la notificación.
   * @returns Un observable que emite la respuesta procesada del servicio Keiron.
   *          En caso de error, retorna un objeto vacío.
   */
  notifyKeiron(formData: FormData) {
    const endpoint = `${BASE_API_PCV2}/${URI_KEIRON_NOTIFICATION}`;
    return this.httpClient.post<PCV2Request>(`${endpoint}`, formData).pipe(
      retry(3),
      map(({ data }) => (data)),
      catchError((error) => of({})),
    );
  };

  /**
   * Notifica a Keiron para cada uno de los elementos en el arreglo de formularios.
   * 
   * @param formsData - Un arreglo de objetos FormData que contienen la información de cada formulario a notificar.
   * @returns Un observable que emite un arreglo con las respuestas de cada notificación a Keiron.
   */
  notifyAllKeiron(formsData: FormData[]) {
    const request = formsData.map(formData => this.notifyKeiron(formData));
    return forkJoin(request);
  };

  /**
   * Construye un objeto FormData con la información necesaria para notificar a Keiron Forms.
   * 
   * @param paperworkState - Estado actual del trámite, que contiene información del paciente, profesional, cita y contenido relevante.
   * @returns Una promesa que resuelve en un objeto FormData con los datos requeridos para la notificación.
   * 
   * El FormData incluye:
   * - Archivo PDF generado.
   * - Sufijo del trámite.
   * - Información del profesional (RUT, nombre, especialidad, centro).
   * - Información de la orden (nombre, número).
   * - Información del paciente (RUT, nombre, fecha de nacimiento, correo electrónico, teléfono).
   * 
   * @remarks
   * Este método depende de la función `getKeironPDFs` para obtener el archivo PDF correspondiente.
   */
  async buildNotifyKeironFormsData(paperworkState): Promise<FormData> {
    const { patient, professional, shortId, suffix, appointment } = paperworkState.paperwork;
    const file = await this.getKeironPDFs(paperworkState);
    const notifyFormData = new FormData();
    
    notifyFormData.append('file', file.file);
    notifyFormData.append('suffix', suffix);
    notifyFormData.append('professionalRut', professional.rut);
    notifyFormData.append('professionalName', `${professional.names || ''} ${professional.surnames || ''}`);
    notifyFormData.append('professionalSpecialty', professional.specialty?.name || '');
    notifyFormData.append('professionalCenter', appointment?.center || '');
    notifyFormData.append('orderName', 'Derivaciones Médicas y No Médicas');
    notifyFormData.append('orderNumber', shortId);
    notifyFormData.append('patientRut', patient.documentNumber || '');
    notifyFormData.append('patientName', `${patient.names || ''} ${patient.surnames || ''}`);
    notifyFormData.append('patientBirthDate', patient.birth || '');
    notifyFormData.append('patientEmail', patient.email || '');
    notifyFormData.append('patientPhone', patient.phone || '');

    return notifyFormData;
  };

  /**
   * Genera y retorna el primer documento PDF de Keiron basado en el estado del trámite proporcionado.
   *
   * Este método agrupa las filas del trámite, construye el contenido PDF para cada grupo,
   * y utiliza el servicio de PDF para crear los archivos en base64. Finalmente, convierte
   * el base64 a un Blob y retorna el primer documento generado con su información asociada.
   *
   * @param paperworkState El estado actual del trámite, incluyendo su definición y datos asociados.
   * @returns Una promesa que resuelve con el primer documento PDF generado, incluyendo su título, extensión, ruta, y archivo en formato Blob.
   */
  async getKeironPDFs(paperworkState: PaperworkState) {
    let docBase64 = null;
    const { definition } = paperworkState;
    const groups = this.groupedRows(paperworkState);
    const docs = { pdfs: [], paperwork: {} as Paperwork };
    const { paperwork } = paperworkState;
    const professional = this.authService.professionalRef;

    for await (const group of groups) {
      const { title } = group;
      const { config, docTitle } = definition.submodule;
      const body = buildContentPdfDerivation(paperwork, group);
      const pdfParams = {
        config: { ...config, isPreview: false },
        paperwork: { ...paperwork, professional },
        group, title, body, docTitle,
      };
      const base64 = await this.pdfService.createPdf(pdfParams);
      const pdf = { base64, title: title + ' Keiron', isAttached: true };

      docBase64 = base64;
      docs.pdfs = [pdf, ...docs.pdfs];
    }

    const extension: FileExtension = 'pdf';
    const documents = docs.pdfs.map((pdf) => ({
      ...pdf,
      extension,
      path: definition.module,
      file: this.pdfService.base64ToBlob(pdf.base64),
    }));
    return documents[0];
  };

  /**
   * Agrupa los formularios contenidos en el objeto `paperwork` según el valor de su propiedad `group`.
   *
   * @param paperwork - Objeto que contiene la propiedad `content`, la cual es un arreglo de formularios. Cada formulario debe tener una propiedad `group`.
   * @returns Un arreglo de objetos, donde cada objeto representa un grupo. Cada grupo contiene un título (`title`) en minúsculas y un arreglo de formularios (`rows`) que pertenecen a ese grupo.
   */
  groupedRows({ paperwork }): any[] {
    const groups: string[] = [...new Set<string>(paperwork.content.map((form) => form?.group))];
    return groups.map<any>((group) => ({
      title: group ? group.toLowerCase() : '',
      rows: paperwork.content.filter((form) => form.group === group),
    }));
  };
}
